import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import getEnv from '../utilities/getEnv'
import qs from 'query-string'
import { IAppErrorResponse } from 'interfaces'
import cookies from 'utilities/cookie'
import {
    ClientVersion,
    CompanyTenantIdentifierID,
    LocationTenantIdentifierID,
    RefreshToken,
    Token,
    XCheftechServerKey,
} from 'types/storeTypes'
import { AuthModel } from 'models'
import { CancelRefreshTokenExpired } from 'types/errorText'
import authExpiredHandler from './authExpiredHandler'
import { API_CANCLED } from 'utilities/APIErrorMessage'
import licenseExpiredEventBus from 'components/LicenseExpiredDialog/licenseExpiredEventBus'
import { isLicenseError } from 'utilities/commonError'
import Interlock from './interlock'
import androidWebViewApi from 'utilities/androidWebViewApi'

/**
 * List of APIs queue to execute again when refreshToken is success
 */
let pendingRequests: {
    originalRequest: AxiosRequestConfig
    axiosInstance: AxiosInstance
    resolve: (value: unknown) => void
    reject: (reason?: any) => void
}[] = []

/**
 * API's request RefreshToken, this flag prevent call multiple RefreshToken
 */
let isRefreshing = new Interlock(false)

/**
 * use broadcastChannel to prevent multiple tabs
 */
const broadcastChannel = new BroadcastChannel('ow_token_channel')

const resolvePendingRequests = () => {
    console.info('begin resolvePendingRequests...')
    pendingRequests.forEach(({ resolve, originalRequest, axiosInstance }) => {
        const newAccessToken = cookies.get(Token)
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        resolve(axiosInstance(originalRequest))
    })
    pendingRequests = []
}

/**
 * This function will cancel all pending requests that are waiting while making call to Refresh token API (isRefreshing = TRUE)
 * This is needed in the edge case that the Refresh Token API failed (e.g., due to expiration was met).
 * In this case, all pending requests must be cancelled.
 * For now, we reject those pending request Promise as `CancelRefreshTokenExpired` error. (the same error used by the request that trigger Refresh Token API)
 */
const cancelPendingRequests = () => {
    console.info('begin cancelPendingRequests...')
    pendingRequests.forEach(({ reject }) => {
        reject({ message: CancelRefreshTokenExpired })
    })
    pendingRequests = []
}

declare module 'axios' {
    export interface AxiosRequestConfig {
        _retry?: boolean
    }
}

const LOCAL_API = getEnv('REACT_APP_LOCAL_API')
const CLOUD_API = getEnv('REACT_APP_CLOUD_API')

const excludeFromRetryUrls = [
    '/client/validate',
    '/auth/login',
    '/auth/social-login',
    '/auth/refresh-token',
    '/client/current-client',
    '/auth/logout',
    '/auth/issue-real-access-token?forceLogout=true',
    '/auth/issue-real-access-token',
]
export interface ApiConfig extends AxiosRequestConfig {
    isAnonymous?: boolean
}
export class HttpRequest {
    axiosInstance: AxiosInstance
    cloudAPI: string
    localAPI: string
    constructor(isAnonymous: boolean = false) {
        this.cloudAPI = CLOUD_API as string
        this.localAPI = LOCAL_API as string
        this.axiosInstance = axios.create({
            timeout: 120000,
            headers: {
                'Content-Type': 'application/json',
            },
            paramsSerializer: (params) => {
                return qs.stringify(params)
            },
        })
        if (!isAnonymous) {
            const token = cookies.get(Token)
            const refreshToken = cookies.get(RefreshToken)
            if (!token || !refreshToken) {
                delete this.axiosInstance.defaults.headers.common[
                    'Authorization'
                ]
            }
        }
        this.axiosInstance.interceptors.request.use(
            // beforeSend callback
            function (config) {
                if (!isAnonymous) {
                    const token = cookies.get(Token)
                    if (token)
                        config.headers['Authorization'] = 'Bearer ' + token
                }
                const COMPANY_TENANT_IDENTIFIER_ID = getEnv(
                    'REACT_APP_COMPANY_TENANT_IDENTIFIER_ID',
                )
                const LOCATION_TENANT_IDENTIFIER_ID = getEnv(
                    'REACT_APP_LOCATION_TENANT_IDENTIFIER_ID',
                )

                if (COMPANY_TENANT_IDENTIFIER_ID)
                    config.headers[
                        LocationTenantIdentifierID
                    ] = LOCATION_TENANT_IDENTIFIER_ID

                if (LOCATION_TENANT_IDENTIFIER_ID)
                    config.headers[
                        CompanyTenantIdentifierID
                    ] = COMPANY_TENANT_IDENTIFIER_ID

                const webVersion = getEnv('WEB_VERSION')
                if (webVersion) {
                    config.headers[ClientVersion] = webVersion
                }

                return config
            },
            // onError callback
            function (error: AxiosError) {
                return Promise.reject(error.response)
            },
        )

        // Add a response interceptor
        this.axiosInstance.interceptors.response.use(
            function (response) {
                return response
            },
            async (error: AxiosError<IAppErrorResponse>) => {
                if (axios.isCancel(error)) return Promise.reject(API_CANCLED)

                // If license is expired, we don't kill user's session
                // Instead we show the dialog to warn user
                if (isLicenseError(error)) {
                    licenseExpiredEventBus.next(true)

                    // tell caller to discard this HTTP request
                    return Promise.reject(API_CANCLED)
                }

                console.log(
                    `handling AuthError when call to: ${error.config.url}`,
                )

                const originalRequest = error.config
                const locationTenant =
                    error.config.headers[LocationTenantIdentifierID]
                const companyTenant =
                    error.config.headers[CompanyTenantIdentifierID]
                const serverKey = error.config.headers[XCheftechServerKey]
                if (
                    // we exclude some URLs e.g. login, refresh-token, logout that not need the retry mechanism
                    !excludeFromRetryUrls.some((s) =>
                        error.config.url?.includes(s),
                    ) &&
                    // do not enter refreshToken process for public API
                    !isAnonymous &&
                    locationTenant &&
                    companyTenant &&
                    serverKey &&
                    // do refreshToken only for Auth Error
                    error.response?.status === 401 &&
                    // prevent same request goto this block
                    !originalRequest._retry
                ) {
                    /**
                     * protect self infinite loop
                     */
                    originalRequest._retry = true

                    if (await isRefreshing.getValue()) {
                        // If a refreshToken is in-progress, add the original request and its corresponding resolve/reject function to the pending requests queue
                        console.log('push request to pendingRequests queue.')

                        return new Promise((resolve, reject) => {
                            pendingRequests.push({
                                resolve,
                                reject,
                                originalRequest,
                                axiosInstance: this.axiosInstance,
                            })
                        })
                    } else await isRefreshing.setValue(true)

                    console.log(
                        'begin refresh token process, set isRefreshing = TRUE',
                    )

                    // Broadcast a message to other tabs to prevent simultaneous refresh requests
                    broadcastChannel.postMessage('start_refresh_token_message')

                    const token = cookies.get(Token)
                    const refreshToken = cookies.get(RefreshToken)

                    try {
                        // skip calling 'refresh-token` with empty token
                        if (!Boolean(token) || !Boolean(refreshToken)) {
                            throw new Error('no token available')
                        }

                        const tempAnonymousHttpClient = new HttpRequest(true)
                        const serverKey = localStorage.getItem(
                            XCheftechServerKey,
                        )
                        if (serverKey === null || serverKey === undefined)
                            throw new Error(
                                'no server key found while refreshing token',
                            )

                        tempAnonymousHttpClient.setHeader({
                            key: XCheftechServerKey,
                            value: serverKey,
                        })
                        const newTokenResult: AuthModel = (
                            await tempAnonymousHttpClient.post<AuthModel>(
                                '/auth/refresh-token',
                                {
                                    accessToken: token,
                                    refreshToken,
                                },
                            )
                        ).data
                        httpRequest.setAuthorizationCookies(
                            newTokenResult.token!,
                            newTokenResult.refreshToken!,
                        )

                        const newAccessToken = newTokenResult.token
                        originalRequest.headers['Authorization'] =
                            'Bearer ' + newAccessToken

                        // release the lock to enabled retry mechanism
                        await isRefreshing.setValue(false)

                        // at this point `token`, `refreshToken` has already set to new one
                        // so, we can safely release all pending requests
                        resolvePendingRequests()

                        return this.axiosInstance(originalRequest)
                    } catch (_error: any) {
                        const overrideErrorMessage = {
                            ..._error,
                            message: CancelRefreshTokenExpired,
                        }

                        // Fail to refresh token using refresh token in cookie then clear token and refresh token
                        this.deleteAuthorizationCookies()

                        // at this point, the refresh token is invalid. Relogin is needed.
                        // currently, we don't have the feature to allow the users to recover the pending requests in the case of expired Refresh token.
                        // so, all we can do is to clear all pending requests
                        cancelPendingRequests()

                        // always call this function without checking for existing of `refreshToken` because another tab may remove the token
                        // the `authExpiredHandler` will then dispatch action setIsAuthExpired (AUTH_EXPIRED) which will set `authState` to `AuthorizationState.EXPIRED`
                        // and will cause redirection to main page
                        authExpiredHandler()

                        await isRefreshing.setValue(false)

                        return Promise.reject(
                            this.attachHttpMetadataToError(
                                overrideErrorMessage,
                                _error,
                            ),
                        )
                    } finally {
                        // Broadcast a message to other tabs to resolve queue with new access token
                        broadcastChannel.postMessage(
                            'end_refresh_token_message',
                        )
                    }
                }

                return Promise.reject(
                    this.attachHttpMetadataToError(
                        error.response?.data || error,
                        error,
                    ),
                )
            },
        )
    }

    setHeader(header: { key: string; value: string }) {
        this.axiosInstance.defaults.headers.common[header.key] = header.value
    }

    removeHeader(key: string) {
        delete this.axiosInstance.defaults.headers.common[key]
    }

    private getSevenDayExpire() {
        return new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    }

    private attachHttpMetadataToError(errorObj: any, originalAxiosError: any) {
        if (typeof errorObj !== 'object') {
            errorObj = {
                message: '' + errorObj,
            }
        }

        errorObj.httpMetadata = {
            url: originalAxiosError?.config?.url,
            method: originalAxiosError?.config?.method,
            status: originalAxiosError?.response?.status,
        }

        return errorObj
    }

    setNonClientAuthorizationCookies(token: string) {
        const oldToken = cookies.get(Token)
        if (!oldToken || oldToken !== token) {
            const expires = this.getSevenDayExpire()
            cookies.set(Token, token, { path: '/', expires })
            androidWebViewApi.updateCookies()
        }
    }

    setAuthorizationCookies(token: string, refreshToken: string) {
        const oldToken = cookies.get(Token)
        const oldRefreshToken = cookies.get(RefreshToken)
        if (!oldToken || oldToken !== token) {
            const expires = this.getSevenDayExpire()
            cookies.set(Token, token, { path: '/', expires })
        }

        if (!oldRefreshToken || oldRefreshToken !== refreshToken) {
            // set the refreshToken lifetime to 7 days
            const expires = this.getSevenDayExpire()
            cookies.set(RefreshToken, refreshToken, { path: '/', expires })
        }
        androidWebViewApi.updateCookies()
    }
    /// check if the user has a token in the cookie (do not care if it valid or not)
    hasAuthorizationToken() {
        return !!cookies.get(Token)
    }

    deleteAuthorizationCookies() {
        cookies.remove(Token, { path: '/' })
        cookies.remove(RefreshToken, { path: '/' })
        delete this.axiosInstance.defaults.headers.common['Authorization']
    }

    get<Type>(methodName: string, config?: ApiConfig) {
        return this.axiosInstance.get<Type>(
            `${this.cloudAPI}${methodName}`,
            config,
        )
    }

    post<Type>(methodName: string, data?: any, config?: ApiConfig) {
        return this.axiosInstance.post<Type>(
            `${this.cloudAPI}${methodName}`,
            data,
            config,
        )
    }

    put<Type>(methodName: string, data?: any, config?: ApiConfig) {
        return this.axiosInstance.put<Type>(
            `${this.cloudAPI}${methodName}`,
            data,
            config,
        )
    }

    delete<Type>(methodName: string, config?: ApiConfig) {
        return this.axiosInstance.delete<Type>(
            `${this.cloudAPI}${methodName}`,
            config,
        )
    }
}

const httpRequest = new HttpRequest()
const httpAnonymousRequest = new HttpRequest(true)

broadcastChannel.addEventListener('message', async (event) => {
    if (event.data === 'start_refresh_token_message') {
        console.log(
            'another tab has begined refreshing token, set isRefreshing = TRUE',
        )
        await isRefreshing.setValue(true)
    } else if (event.data === 'end_refresh_token_message') {
        console.log(
            'another tab has finished refreshing token, set isRefreshing = FALSE',
        )
        await isRefreshing.setValue(false)

        // If a refresh request has completed in another tab, process pending request queue
        resolvePendingRequests()
    }
})

export { httpAnonymousRequest }
export default httpRequest
